import React from 'react';
import {emptyArray} from '@packages/utilities/fp/utils';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {
  WF_COLLECTION_DATA_KEY,
  WF_TEMPLATE_ID_DATA_KEY,
  WF_TEMPLATE_TYPE,
} from '@packages/systems/commerce/constants';
import {
  withMeta,
  Plugins,
  withSystem,
  Atom,
  ContextType,
  ChildRender,
} from '@packages/systems/wfdl';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {iconName, typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {getCommerceDataProperty} from './ContextUtils';
import {
  TListOfElement,
  TTypeConstructor,
  TRecord,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment, useTemplate} from '@packages/systems/render';
import {EvaluatedNodeData, getChildren} from '@packages/utilities/data-utils';

const sampleDownloads = [{name: '', url: ''}];

const getDownloadFiles = getCommerceDataProperty('downloadFiles', []);

const renderDownloadItem =
  ({
    children,
    context,
    renderChild,
  }: {
    children: EvaluatedNodeData[];
    context: ContextType;
    renderChild: ChildRender;
  }) =>
  (item: unknown, index: number | undefined) =>
    children.map((child) =>
      renderChild(
        child,
        // TODO (ecommerce): Memoize commerce context mutations
        context.set('data', item),
        `download-file-${index}`,
        index
      )
    );

const renderDownloadItems = ({
  renderChild,
  context,
  data,
  downloadFiles,
}: {
  renderChild: ChildRender;
  context: ContextType;
  data: EvaluatedNodeData;
  downloadFiles: Record<string, unknown>[];
}) => {
  return downloadFiles.map(
    renderDownloadItem({
      children: getChildren(data),
      context,
      renderChild,
    })
  );
};

const CommerceDownloadsList = Atom.createRender(
  ({
    context: prevContext,
    attributes,
    classes,
    data,
    generateAttributes,
    elementId,
    renderChild,
  }) => {
    const downloadFiles = getDownloadFiles(prevContext) || [];

    // TODO (ecommerce): Memoize commerce context mutations
    const context = prevContext.update(
      'bindingPathPrefix',
      emptyArray,
      (bindingPathPrefix) => bindingPathPrefix.concat('downloadFiles[]')
    );

    const template = useTemplate();
    const runtimeEnvironment = useRuntimeEnvironment();

    if (
      runtimeEnvironment === WebflowEnvironments.SSR ||
      runtimeEnvironment === WebflowEnvironments.EXPORT
    ) {
      const content = renderDownloadItems({
        renderChild,
        context,
        data,
        downloadFiles: sampleDownloads,
      });
      const templateContent = template(content);
      const tagAttributes = {
        ...generateAttributes(attributes, classes),
        [WF_COLLECTION_DATA_KEY]: 'database.commerceOrder.downloadFiles',
        [WF_TEMPLATE_ID_DATA_KEY]: `wf-template-${elementId}`,
      } as const;

      return (
        <>
          <script type={WF_TEMPLATE_TYPE} id={`wf-template-${elementId}`}>
            {templateContent}
          </script>
          <div {...tagAttributes}>{content}</div>
        </>
      );
    }
    return (
      <div {...generateAttributes(attributes, classes)}>
        {renderDownloadItems({
          renderChild,
          context,
          data,
          downloadFiles,
        })}
      </div>
    );
  }
);

const config = createIntegratedAtom({
  displayName: 'Downloads List',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Dynamo'), iconName('CollectionList')),
  },
  showEmpty: false,
  render: CommerceDownloadsList,
  constraints: {
    children: [
      {
        is: 'CommerceDownloadsItem',
        name: 'Element',
        rule: 'RequireOnly',
        messages: {
          paste:
            'Paste this element inside the Downloads Item element to add it to each Downloads item.',
          move: 'Move this element inside the Downloads Item element to add it to each Downloads item.',
        },
      },
      {is: 'CommerceDownloadsItem', rule: 'ExactlyOne'},
    ],
  },
  style: {
    base: {
      main: {
        noPseudo: {
          // Used to negate the marginBottom of last list item
          marginBottom: px(-20),
        },
      },
    },
  },
  fields: {
    source: withMeta(
      {
        source: 'Downloads',
      },
      TTypeConstructor(
        qualify(Plugins.name('Commerce'), typeName('LockedDynSource'))
      )
    ),
    search: withSystem(
      {private: true},
      TRecord({
        exclude: excludeSearchByDefaultType,
      })
    ),
    children: TListOfElement,
  },
});

export default config;
